import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const portfolioItems = [
  // Case Study - Unterweisungssystem
  {
    id: 'instruction-system-case-study',
    title: { de: 'Case Study: Digitale Schulungen für verpflichtende Unterweisungen', en: 'Case Study: Digital Training for Mandatory Instructions' },
    category: 'unterweisungssystem',
    image: '/assets/instruction-system.jpeg',
    imagePosition: 'object-[75%_30%]',
    hasDetailPage: true,
    detailLink: '/case-study/instruction-system'
  },
  // Case Study - Besucherunterweisungssystem
  {
    id: 'visitor-system-case-study',
    title: { de: 'Case Study: Digitales Besucher- und Fremdfirmenunterweisungssystem', en: 'Case Study: Digital Visitor and Third-Party Instruction System' },
    category: 'besucherunterweisung',
    image: '/assets/visitor/images/img_1_1.jpeg',
    hasDetailPage: true,
    detailLink: '/case-study/visitor-system'
  },
  // Case Study - IT-Security (now under Unterweisungssystem)
  {
    id: 'cyber-security-case-study',
    title: { de: 'Case Study: IT-Security Awareness Training für internationale Standorte', en: 'Case Study: IT-Security Awareness Training for International Locations' },
    category: 'unterweisungssystem',
    image: '/assets/instruction-system.jpeg',
    imagePosition: 'object-[75%_30%]',
    hasDetailPage: true,
    detailLink: '/case-study/cyber-security'
  }
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

export default function PortfolioPage() {
  const { language, t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: language === 'de' ? 'Alle' : 'All' },
    { id: 'unterweisungssystem', label: language === 'de' ? 'Unterweisungssystem' : 'Instruction System' },
    { id: 'besucherunterweisung', label: language === 'de' ? 'Besucherunterweisung' : 'Visitor Instruction' }
  ];

  const filteredItems = activeCategory === 'all'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === activeCategory);

  return (
    <div className="min-h-screen pt-20" data-testid="portfolio-page">
      <SEO
        title={language === 'de' ? 'Portfolio - E-Learning Referenzprojekte & Case Studies' : 'Portfolio - E-Learning Reference Projects & Case Studies'}
        description={language === 'de' 
          ? 'Referenzprojekte und Fallstudien von e-Matrix Innovations: Unterweisungssystem, Besucherunterweisung, Cyber Security Awareness. Erfolgsgeschichten aus Österreich.' 
          : 'Reference projects and case studies from e-Matrix Innovations: Training system, visitor instruction, cyber security awareness. Success stories from Austria.'}
        keywords={language === 'de'
          ? 'E-Learning Portfolio, Referenzprojekte Unterweisungssystem, Case Study Arbeitssicherheit, Erfolgsgeschichten E-Learning, Cyber Security Awareness Projekt, Besucherunterweisung Praxisbeispiel'
          : 'E-Learning Portfolio, Training System Reference Projects, Workplace Safety Case Study, E-Learning Success Stories, Cyber Security Awareness Project, Visitor Instruction Example'}
        path="/portfolio"
        lang={language}
      />
      <section className="py-20 md:py-28 bg-[#E7F0FA]" data-testid="portfolio-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0D2440] mb-6">
              {language === 'de' ? 'Unsere Projekte' : 'Our Projects'}
            </h1>
            <p className="text-lg text-[#5A7A9B] max-w-3xl mx-auto">
              {language === 'de'
                ? 'Entdecken Sie unsere erfolgreich umgesetzten E-Learning-Projekte und lassen Sie sich von unseren Lösungen inspirieren.'
                : 'Discover our successfully implemented e-learning projects and get inspired by our solutions.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 md:py-28 bg-white" data-testid="portfolio-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#2E5E99] text-white'
                    : 'bg-[#E7F0FA] text-[#5A7A9B] hover:bg-[#D0E0F0]'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Portfolio Items */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => (
              <Link 
                key={item.id}
                to={item.hasDetailPage ? item.detailLink : '/booking'}
                data-testid={`portfolio-item-${item.id}`}
              >
                <motion.div
                  className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-[#C8D8E8] cursor-pointer h-full"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.1, 0.5) }}
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={item.image}
                      alt={item.title[language]}
                      className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${item.imagePosition || ''}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-semibold tracking-wider text-[#2E5E99] uppercase">
                      {categories.find(c => c.id === item.category)?.label}
                    </span>
                    <h3 className="font-semibold text-xl text-[#0D2440] mt-2 group-hover:text-[#2E5E99] transition-colors">
                      {item.title[language]}
                    </h3>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 md:py-28 bg-[#0D2440]" data-testid="portfolio-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { value: '100+', label: language === 'de' ? 'Projekte' : 'Projects' },
              { value: '200+', label: language === 'de' ? 'Zufriedene Kunden' : 'Happy Clients' },
              { value: '10+', label: language === 'de' ? 'Sprachen' : 'Languages' }
            ].map((stat, index) => (
              <motion.div
                key={stat.value}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-white/70">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#2E5E99]" data-testid="portfolio-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {language === 'de'
                ? 'Starten Sie Ihr nächstes Projekt'
                : 'Start Your Next Project'}
            </h2>
            <p className="text-lg text-white/80 mb-10">
              {language === 'de'
                ? 'Kontaktieren Sie uns und werden Sie Teil unserer Erfolgsgeschichten.'
                : 'Contact us and become part of our success stories.'}
            </p>
            <Link to="/contact" data-testid="portfolio-contact-cta">
              <Button className="bg-white text-[#2E5E99] hover:bg-white/90 h-12 px-8 text-base">
                {language === 'de' ? 'Projekt starten' : 'Start Project'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
